import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge, fmtUSD, fmtDate } from "@/components/ui-bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminDeposits() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [preview, setPreview] = useState(null);
  const [blobUrl, setBlobUrl] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/deposits${filter ? `?status=${filter}` : ""}`);
    setRows(data);
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id, action) => {
    try { await api.post(`/admin/deposits/${id}/${action}`); toast.success("Done"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openPreview = async (dep) => {
    setPreview(dep);
    setBlobUrl("");
    if (dep.screenshot_path) {
      try {
        const r = await api.get(`/files/${dep.screenshot_path}`, { responseType: "blob" });
        setBlobUrl(URL.createObjectURL(r.data));
      } catch { /* preview unavailable */ }
    }
  };
  const closePreview = () => { setPreview(null); if (blobUrl) URL.revokeObjectURL(blobUrl); setBlobUrl(""); };

  return (
    <div>
      <PageHeader title="Deposit Approvals"
        action={
          <div className="flex flex-wrap gap-2">
            {["pending", "approved", "rejected", ""].map((f) => (
              <button key={f || "all"} onClick={() => setFilter(f)} data-testid={`filter-${f || "all"}`}
                      className={`px-2.5 sm:px-3 py-1.5 text-[10px] sm:text-xs uppercase tracking-wider rounded-lg border ${filter === f ? "bg-[#00F0FF] text-[#060B19] border-[#00F0FF]" : "bg-[#0A132A] border-white/10 text-[#94A3B8] hover:border-[#00F0FF]/40"}`}>
                {f || "All"}
              </button>
            ))}
          </div>
        }
      />
      <div className="glass p-5 overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="text-[11px] uppercase tracking-wider text-[#475569]">
            <tr className="border-b border-white/5">
              <th className="text-left py-2">User</th><th className="text-left">Amount</th>
              <th className="text-left">TX Hash</th><th className="text-left">Proof</th>
              <th className="text-left">Date</th><th className="text-right">Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="admin-deposits-table">
            {rows.map((d) => (
              <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-3">
                  <div className="font-semibold text-sm">{d.user?.name}</div>
                  <div className="text-xs text-[#94A3B8]">{d.user?.email}</div>
                </td>
                <td className="font-mono">{fmtUSD(d.amount)}</td>
                <td className="font-mono text-xs text-[#94A3B8] truncate max-w-[160px]">{d.tx_hash}</td>
                <td>
                  {d.screenshot_path ? (
                    <button onClick={() => openPreview(d)} data-testid={`preview-${d.id}`} className="text-[#00F0FF] text-xs hover:underline">View</button>
                  ) : <span className="text-[#475569] text-xs">none</span>}
                </td>
                <td className="text-[#94A3B8]">{fmtDate(d.created_at)}</td>
                <td className="text-right"><StatusBadge status={d.status} /></td>
                <td className="text-right">
                  {d.status === "pending" && (
                    <div className="inline-flex gap-2">
                      <button onClick={() => act(d.id, "approve")} data-testid={`approve-${d.id}`} className="px-2.5 py-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded text-xs hover:bg-emerald-500/25">Approve</button>
                      <button onClick={() => act(d.id, "reject")} data-testid={`reject-${d.id}`} className="px-2.5 py-1 bg-red-500/15 text-red-300 border border-red-500/30 rounded text-xs hover:bg-red-500/25">Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-[#475569]">No deposits</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="bg-[#0A132A] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">Deposit Proof</DialogTitle></DialogHeader>
          {blobUrl ? (
            <img src={blobUrl} alt="proof" className="w-full rounded-lg border border-white/10" />
          ) : (
            <div className="text-[#94A3B8] py-10 text-center">Loading…</div>
          )}
          {preview && (
            <div className="text-xs text-[#94A3B8] mt-2 space-y-1">
              <div>Amount: <span className="text-white font-mono">{fmtUSD(preview.amount)}</span></div>
              <div>TX: <span className="font-mono break-all">{preview.tx_hash}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
